import React, { FC, useId } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import AddIcon from '@mui/icons-material/Add';
import AttachmentIcon from '@mui/icons-material/Attachment';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadingIcon from '@mui/icons-material/Downloading';
import ErrorIcon from '@mui/icons-material/Error';
import FilePresentOutlinedIcon from '@mui/icons-material/FilePresentOutlined';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import InfoIcon from '@mui/icons-material/Info';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import {
  ButtonProps,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { bindMenu, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';

import Tooltip from '@/components/Tooltip';
import {
  DEFAULT_MAX_UPLOADS_COUNT,
  DEFAULT_VALID_FILE_EXTENSIONS,
} from '@/features/file-attachments/config';
import { useFileAttachments } from '@/features/file-attachments/hooks/useFileAttachments';

import { FileAttachmentCallbacks } from '../types/FileAttachmentCallbacks';
import { OcrFileViewModel } from '../types/OcrFileViewModel';
import { UploadedFileViewModel } from '../types/UploadedFileViewModel';

type CustomLabelProps = ButtonProps & {
  hasAttachments: boolean;
};

interface Props {
  /** Whether there are attachments to display */
  hasAttachments: boolean;
  /** Callbacks for fetching and viewing attachments */
  callbacks: FileAttachmentCallbacks;
  /**
   * Optional array of accepted extensions without periods; e.g., `['pdf','image/jpeg', 'image/gif']`.
   * If not specified then all files can be uploaded.
   *
   * @see {@link DEFAULT_VALID_FILE_EXTENSIONS} for defaults
   */
  validFileExtensions?: Array<string>;
  /** Whether to allow file uploads */
  allowFileUpload?: boolean;
  /** @see {@link DEFAULT_MAX_UPLOADS_COUNT} for default */
  maxUploadsCount?: number;
  /** Previously uploaded files to display */
  uploadedFiles?: Array<UploadedFileViewModel>;
  /** OCR file to display */
  ocrFile?: OcrFileViewModel;
  /** Custom label component to use for triggering the menu (instead of the default icon button) */
  CustomLabel?: React.FC<CustomLabelProps>;
  /**
   * Fetch the attachments when loading this fragment?
   * True by default where a payment or receivable ID is available
   * False on cases where a payment or receivable is about to be created (no ID)
   */
  shouldFetchOnLoad?: boolean;
  /** Callback to notify when an arbitrary field has changed */
  onFieldChanged?: (fieldName: string, fieldValue: unknown) => void;
}

/**
 * A component to display and upload file attachments.
 */
const FileAttachments: FC<Props> = ({
  allowFileUpload = true,
  callbacks,
  hasAttachments,
  maxUploadsCount = DEFAULT_MAX_UPLOADS_COUNT,
  ocrFile,
  shouldFetchOnLoad = true,
  uploadedFiles: previouslyUploadedFiles = [],
  validFileExtensions = DEFAULT_VALID_FILE_EXTENSIONS,
  onFieldChanged,
  CustomLabel = null,
}) => {
  const menuId = useId();
  const menuState = usePopupState({ variant: 'popover', popupId: menuId });
  const shouldFetchAttachments = menuState.isOpen && shouldFetchOnLoad;

  const {
    attachments,
    attachmentsError,
    attachmentsIsLoading: isFetchingAttachments,
    refetchAttachments,
    attachmentsIsSuccess: hasFetchedAttachments,
    prettyMaxFileSize,
    fileInputRef,
    uploadedFiles,
    isUploadingAttachments,
    deleteAttachment,
    handleAttachmentUpload,
    handleConfirmUpload,
    handleViewUploadedFile,
    handleViewAttachment,
    handleViewOcrAttachment,
    isMaxFilesUploaded,
    validFileTypes,
    displayConfirmButton,
    hasAttachmentsToDisplay,
  } = useFileAttachments({
    allowFileUpload,
    callbacks,
    maxUploadsCount,
    ocrFile,
    shouldFetchAttachments,
    uploadedFilesInitialValue: previouslyUploadedFiles,
    validFileExtensions,
    onFieldChanged,
  });

  const intl = useIntl();

  /** We don't want to render the component if there are no attachments nor ability to upload */
  const shouldDisableComponent =
    !hasAttachments && !allowFileUpload && !ocrFile && !(previouslyUploadedFiles?.length > 0);
  if (shouldDisableComponent) {
    return null;
  }

  const getConfirmUploadButton = () =>
    displayConfirmButton ? (
      <MenuItem dense onClick={handleConfirmUpload} key="confirm-button">
        <ListItemText sx={{ color: 'primary.main', textAlign: 'center' }}>
          <FormattedMessage defaultMessage="Confirm Upload" />
        </ListItemText>
      </MenuItem>
    ) : null;

  const getOptions = () => {
    if (isFetchingAttachments) {
      return (
        <MenuItem disabled key="loading">
          <ListItemIcon>
            <DownloadingIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <FormattedMessage defaultMessage="Fetching Attachments ..." />
          </ListItemText>
        </MenuItem>
      );
    }

    if (attachmentsError) {
      return (
        <MenuItem onClick={() => refetchAttachments()} key="fetch-error">
          <ListItemIcon>
            <ErrorIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <FormattedMessage defaultMessage="Error Fetching Attachments..." />
          </ListItemText>
        </MenuItem>
      );
    }

    if (hasFetchedAttachments) {
      if (!hasAttachmentsToDisplay) {
        return (
          <MenuItem disabled key="no-attachments">
            <ListItemIcon>
              <InfoIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              <FormattedMessage defaultMessage="No Attachments" />
            </ListItemText>
          </MenuItem>
        );
      }

      return [
        ocrFile && (
          <MenuItem
            onClick={() => handleViewOcrAttachment(ocrFile.documentFileId)}
            key={ocrFile.documentFileId}
          >
            <ListItemIcon>
              <FilePresentOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{ocrFile.fileName}</ListItemText>
          </MenuItem>
        ),
        Array.isArray(attachments)
          ? attachments.map((attachment, index) => (
              <MenuItem
                // Attachment has no identifier.
                // eslint-disable-next-line react/no-array-index-key
                key={index + (ocrFile ? 1 : 0)}
                data-testid="attachment-menu-item"
                onClick={() => handleViewAttachment(attachment)}
              >
                <ListItemIcon>
                  <FilePresentOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{attachment.name}</ListItemText>
              </MenuItem>
            ))
          : null,
        !allowFileUpload &&
          uploadedFiles
            .filter((file) => Boolean(file.companyFileStorageFileId))
            .map((file) => (
              <MenuItem
                key={file.companyFileStorageFileId}
                onClick={() => handleViewUploadedFile(file)}
              >
                <ListItemIcon>
                  <UploadFileOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{file.fileName}</ListItemText>
              </MenuItem>
            )),
      ].filter(Boolean);
    }

    return null;
  };

  const getUploadOptions = () => {
    if (!allowFileUpload) {
      return null;
    }

    const getDivider = () =>
      // Only show divider if there are bill attachments (to provide some separation)
      attachments?.length > 0 ? <Divider /> : null;

    const getUploadButton = () => {
      // Limit number of allowed uploads
      if (isMaxFilesUploaded) {
        return [
          <MenuItem disabled key="max-uploads">
            <ListItemIcon>
              <InfoIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              <FormattedMessage
                defaultMessage="Maximum files ({max}) uploaded"
                values={{ max: maxUploadsCount }}
              />
            </ListItemText>
          </MenuItem>,
          <Divider key={1} />,
          getConfirmUploadButton(),
        ].filter(Boolean);
      }

      if (isUploadingAttachments) {
        return (
          <MenuItem disabled key="uploading">
            <ListItemIcon>
              <FileUploadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              <FormattedMessage defaultMessage="Uploading ..." />
            </ListItemText>
          </MenuItem>
        );
      }

      return [
        <MenuItem component="label" key="upload-attachment">
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <FormattedMessage
              defaultMessage="Upload attachment (max. {maxFileSize})"
              values={{ maxFileSize: prettyMaxFileSize }}
            />
          </ListItemText>
          <input
            ref={fileInputRef}
            type="file"
            accept={validFileTypes}
            onChange={handleAttachmentUpload}
            style={{ display: 'none' }}
          />
        </MenuItem>,
        getConfirmUploadButton(),
      ].filter(Boolean);
    };

    const dispatchAccountingSoftwareMenuItemClick = (uploadedFile: UploadedFileViewModel) => {
      // View Accounting Software attachment
      const attachment = {
        name: uploadedFile.fileName,
        contentType: uploadedFile.contentType,
        url: uploadedFile.url,
      };
      handleViewAttachment(attachment);
    };

    const getUploadedFiles = () => {
      if (uploadedFiles.length === 0) {
        return null;
      }

      const menuItems = uploadedFiles.map((file) =>
        file.companyFileStorageFileId ? (
          <MenuItem
            onClick={() => handleViewUploadedFile(file)}
            key={file.companyFileStorageFileId}
            data-testid="uploaded-file-menu-item"
          >
            <ListItemIcon>
              <FilePresentOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{file.fileName}</ListItemText>

            <Tooltip
              title={intl.formatMessage(
                { defaultMessage: 'Delete "{fileName}"' },
                { fileName: file.fileName }
              )}
            >
              <IconButton
                onClick={(event) => {
                  // Prevent selection of the attachment
                  event.stopPropagation();

                  deleteAttachment(file);
                }}
                sx={{ ml: 1 }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </MenuItem>
        ) : (
          <MenuItem
            // Our attachment URLs are unique
            key={file.url}
            onClick={() => dispatchAccountingSoftwareMenuItemClick(file)}
          >
            <ListItemIcon>
              <FilePresentOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{file.fileName}</ListItemText>
          </MenuItem>
        )
      );

      return [menuItems, <Divider key="options-divider" />];
    };

    return [getDivider(), getUploadedFiles(), getUploadButton()];
  };

  return (
    <>
      <Tooltip title={intl.formatMessage({ defaultMessage: 'View Attachments' })} placement="top">
        {CustomLabel ? (
          <CustomLabel
            hasAttachments={Boolean(uploadedFiles?.length)}
            {...bindTrigger(menuState)}
          />
        ) : (
          <IconButton size="small" {...bindTrigger(menuState)}>
            <AttachmentIcon />
          </IconButton>
        )}
      </Tooltip>
      <Menu data-testid="attachments-menu" sx={{ menu: { mt: 5 } }} {...bindMenu(menuState)}>
        {getOptions()}
        {getUploadOptions()}
      </Menu>
    </>
  );
};

export type { Props as FileAttachmentsProps };
export { FileAttachments, DEFAULT_MAX_UPLOADS_COUNT };
